import { useState } from 'react';
import { useAction } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { IInviteCompanyMemberDialogProps, TCompanyMemberRole } from '@/@types/company';

const AVAILABLE_ROLES: { value: TCompanyMemberRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'employee', label: 'Employee' },
  { value: 'accountant', label: 'Accountant' },
];

export function InviteCompanyMemberDialog({
  open,
  onOpenChange,
  companyId,
}: IInviteCompanyMemberDialogProps) {
  const sendInvitation = useAction(api.companies.invitationActions.sendCompanyInvitation);

  const [email, setEmail] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<TCompanyMemberRole[]>([]);
  // LOCAL_STATE_JUSTIFIED: simple dialog form — email + roles are tightly coupled to this isolated form

  const [isSending, setIsSending] = useState(false);

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      setEmail('');
      setSelectedRoles([]);
    }
    onOpenChange(value);
  };

  const handleRoleToggle = (role: TCompanyMemberRole) => {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || selectedRoles.length === 0) return;

    setIsSending(true);
    try {
      const result = await sendInvitation({
        companyId,
        email: trimmedEmail,
        roles: selectedRoles,
      });

      if (result.success) {
        toast.success(`Invitation sent to ${trimmedEmail}`);
        handleOpenChange(false);
      } else {
        toast.error(result.error ?? 'Failed to send invitation');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogDescription>
            Send an invitation email to add a new member to your company.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="member-email">Email Address</Label>
            <Input
              id="member-email"
              type="email"
              placeholder="colleague@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={isSending}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Roles (select at least one)</Label>
            <div className="space-y-2">
              {AVAILABLE_ROLES.map(role => (
                <div key={role.value} className="flex items-center gap-2">
                  <Checkbox
                    id={`role-${role.value}`}
                    checked={selectedRoles.includes(role.value)}
                    onCheckedChange={() => handleRoleToggle(role.value)}
                    disabled={isSending}
                  />
                  <Label htmlFor={`role-${role.value}`} className="text-sm font-normal cursor-pointer">
                    {role.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSending || !email.trim() || selectedRoles.length === 0}
            >
              {isSending && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
              Send Invitation
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
